import { useQuery } from '@tanstack/react-query'
import { fetchMyAsks, MY_ASKS_KEY, type AskPart } from '../api/asks'
import { fetchContent, text } from '../api/content'
import AskRow from './AskRow'

/** Whether anything on the one list is this account's to answer (D-543). */
export function useAsksWaitingOnMe(): boolean {
  const mine = useQuery({ queryKey: MY_ASKS_KEY, queryFn: fetchMyAsks })
  return (mine.data ?? []).some((r) => r.myPart === 'owner')
}

/**
 * What is waiting on this account, on Home (D-543, gate Q2).
 *
 * A parent has no Requests screen: the one or two things ever waiting on a
 * household — a makeup to name, a private lesson's time to confirm — sit above
 * the schedule they came to read, answered where they are seen. The rows are
 * the same `AskRow` a teacher answers on `/asks`, from the same list, so a
 * kind built for staff is a kind built for families with nothing added.
 *
 * **Which sides show is the screen's choice, not the kind's** (`parts`). A
 * family's Home shows what they are waiting on as well — the private they
 * asked for, still with the teacher — because it is the only list they have
 * (D-553). A teacher's Home shows only what is hers to answer: what she is
 * waiting on is on `/asks`, and her cover requests already have a card here.
 *
 * Renders nothing at all when nothing waits, and nothing on a failed lookup:
 * this only ever appears when something is owed, so silence is the right
 * answer to both, and "we could not check" on every visit would be noise.
 */
export default function AsksOnHome({ parts = ['owner'] }: { parts?: readonly AskPart[] }) {
  const { data: content } = useQuery({ queryKey: ['content'], queryFn: fetchContent })
  const mine = useQuery({ queryKey: MY_ASKS_KEY, queryFn: fetchMyAsks })

  const rows = mine.data ?? []
  const owner = parts.includes('owner') ? rows.filter((r) => r.myPart === 'owner') : []
  const requester = parts.includes('requester') ? rows.filter((r) => r.myPart === 'requester') : []
  if (owner.length === 0 && requester.length === 0) return null

  return (
    <>
      {owner.length > 0 && (
        <section
          aria-labelledby="asks-on-home"
          className="rounded-2xl border border-warn bg-warn-bg px-4 py-3.5"
        >
          <h2 id="asks-on-home" className="font-display text-xl">
            {text(content, 'asks.home.heading', 'Waiting on you')}
          </h2>

          <ul className="mt-3 flex flex-col gap-3">
            {owner.map((row) => (
              <AskRow key={`${row.kind}:${row.id}`} row={row} content={content} />
            ))}
          </ul>
        </section>
      )}

      {requester.length > 0 && (
        <section
          aria-labelledby="asks-on-home-waiting"
          className="rounded-2xl border border-line bg-surface px-4 py-3.5"
        >
          <h2 id="asks-on-home-waiting" className="font-display text-xl">
            {text(content, 'asks.home.waitingOn', 'You are waiting on')}
          </h2>

          <ul className="mt-3 flex flex-col gap-3">
            {requester.map((row) => (
              <AskRow key={`${row.kind}:${row.id}`} row={row} content={content} />
            ))}
          </ul>
        </section>
      )}
    </>
  )
}
